import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Progress } from "./components/ui/progress";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Github, Linkedin, Mail, Sparkles, Zap, Heart, Code, Rocket, Star, Briefcase, BookOpen, ExternalLink, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PixelTrail } from "./components/ui/pixel-trail";
import { useScreenSize } from "./components/hooks/use-screen-size";

function App() {
  const [coins, setCoins] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [viewedProjectIds, setViewedProjectIds] = useState([]);
  const [viewedPublicationIds, setViewedPublicationIds] = useState([]);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [secretPassword, setSecretPassword] = useState('');
  const [secretError, setSecretError] = useState('');
  const [secretAccessGranted, setSecretAccessGranted] = useState(false);
  const [isSecretSubmitting, setIsSecretSubmitting] = useState(false);
  const [ipLogs, setIpLogs] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [currentTaglineIndex, setCurrentTaglineIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark mode
  const screenSize = useScreenSize();

  const taglines = [
    "Turning coffee into code",
    "Debugging is my cardio",
    "Making machines since 2018",
    "Connecting students, building communities",
    "Exploring the tech-verse"
  ];

  useEffect(() => {
    // Apply dark mode immediately on mount
    document.documentElement.classList.add('dark');
    
    // Log page load event
    logIpEvent('page-loaded');
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTaglineIndex((prev) => (prev + 1) % taglines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Apply dark mode class to document
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const skills = [
    { name: "Python", level: 95, color: "--sky-blue", icon: "🐍" },
    { name: "TensorFlow & PyTorch", level: 90, color: "--mint-green", icon: "🧠" },
    { name: "React & HTML5", level: 85, color: "--coin-gold", icon: "⚛️" },
    { name: "PostgreSQL & Databases", level: 80, color: "--heart-red", icon: "🗄️" },
    { name: "CNN & Deep Learning", level: 92, color: "--primary", icon: "🔬" },
    { name: "Keras & ML Frameworks", level: 88, color: "--accent", icon: "🤖" }
  ];

  const projects = [
    {
      id: 1,
      title: "DICOM SNR CNR Tool",
      description: "GUI tool to review DICOM images, draw ROIs for signal and noise, and export SNR/CNR results to Excel.",
      tags: ["Medical Imaging", "GUI Tool", "Python"],
      coins: 27,
      details: "A comprehensive GUI tool designed for reviewing DICOM images with advanced region of interest (ROI) analysis capabilities. Enables users to draw ROIs for signal and noise measurements, automatically calculate Signal-to-Noise Ratio (SNR) and Contrast-to-Noise Ratio (CNR), and export quantitative results to Excel spreadsheets. Built to streamline medical image quality assessment and analysis workflows.",
      link: "https://github.com/VisheshVarshney/dicom-snr-cnr-tool",
      demoLink: null
    },
    {
      id: 2,
      title: "MUJ Connect",
      description: "A comprehensive social media platform for college students, facilitating connections and information sharing.",
      tags: ["Social Media", "Full-Stack", "Community"],
      coins: 25,
      details: "A comprehensive social media platform designed specifically for college students at Manipal University Jaipur. Features include student profiles, event management, information sharing, and community building tools. Built to connect students and enhance campus life.",
      link: "https://mujconnect.in/",
      demoLink: "https://mujconnect.in/"
    },
    {
      id: 3,
      title: "MRI Stroke Classification",
      description: "Deep learning models to classify MRI scans for stroke detection using advanced neural networks.",
      tags: ["Deep Learning", "Medical AI", "TensorFlow"],
      coins: 30,
      details: "Developed advanced deep learning models utilizing Convolutional Neural Networks (CNNs) to classify MRI scans for accurate stroke detection. The system employs state-of-the-art neural network architectures to assist in early diagnosis and treatment planning, potentially saving lives through rapid and accurate medical image analysis.",
      link: "https://github.com/VisheshVarshney/mri-stroke-classification",
      demoLink: null
    },
    {
      id: 4,
      title: "Brain Tumor Detector",
      description: "Machine learning project detecting benign and malignant tumors for early diagnosis.",
      tags: ["Machine Learning", "Medical AI", "CNN"],
      coins: 28,
      details: "A sophisticated machine learning project capable of detecting and classifying benign and malignant brain tumors from medical imaging data. Provides crucial assistance in early diagnosis and treatment planning by leveraging advanced CNN architectures and image processing techniques.",
      link: "https://github.com/VisheshVarshney/brain-tumor-detector",
      demoLink: null
    },
    {
      id: 5,
      title: "Retinal Vessel Segmentation",
      description: "Classical image processing techniques for retinal vessel extraction.",
      tags: ["Image Processing", "Medical Imaging", "Python"],
      coins: 20,
      details: "Implemented classical image processing techniques for accurate retinal vessel extraction and segmentation. This project aids in the diagnosis of various eye diseases and conditions by providing detailed analysis of retinal blood vessel patterns using advanced computer vision algorithms.",
      link: "https://github.com/VisheshVarshney/retinal-vessel-segmentation",
      demoLink: null
    }
  ];

  const publications = [
    {
      id: 1,
      title: "Utility of T2 and CEST imaging-based radiomics analysis in differentiating healthy and diseased brain tissues on 1.5T MRI",
      description: "Radiomics analysis using T2 and CEST imaging to differentiate healthy and diseased brain tissues on 1.5T MRI",
      details: "This research investigates the utility of T2 and CEST (Chemical Exchange Saturation Transfer) imaging-based radiomics analysis for differentiating healthy and diseased brain tissues on 1.5T MRI systems. The study employs advanced radiomic feature extraction and machine learning techniques to enhance diagnostic accuracy in neuroimaging.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 15
    },
    {
      id: 2,
      title: "Radiomics in ILD: Distinguishing Healthy-Looking Parenchymal Tissue in Patients from Healthy Controls",
      description: "Radiomic analysis for distinguishing healthy-looking parenchymal tissue in ILD patients from healthy controls",
      details: "This study explores the application of radiomics in Interstitial Lung Disease (ILD) to distinguish healthy-looking parenchymal tissue in patients from healthy controls. The research employs machine learning algorithms to analyze imaging features and improve diagnostic capabilities in chest imaging.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 15
    },
    {
      id: 3,
      title: "Automated volumetric MRI analysis to differentiate between Alzheimer's disease patients with and without diabetes",
      description: "Automated volumetric MRI analysis for differentiating Alzheimer's disease patients with and without diabetes",
      details: "This research presents an automated volumetric MRI analysis framework to differentiate between Alzheimer's disease patients with and without diabetes. The study leverages deep learning and image processing techniques to analyze brain volumetric changes and identify distinguishing features associated with comorbidity.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 18
    },
    {
      id: 4,
      title: "Longitudinal neuroimaging and machine learning for differentiating cognitive decline stages",
      description: "Longitudinal neuroimaging combined with machine learning to differentiate stages of cognitive decline",
      details: "This study utilizes longitudinal neuroimaging data combined with machine learning algorithms to differentiate between various stages of cognitive decline. The research focuses on tracking temporal changes in brain structure and function to improve early diagnosis and staging of cognitive impairment.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 18
    },
    {
      id: 5,
      title: "AI-driven longitudinal analysis of Amygdala and Thalamus atrophy in Alzheimer's disease",
      description: "AI-driven longitudinal analysis of Amygdala and Thalamus atrophy patterns in Alzheimer's disease",
      details: "This research employs artificial intelligence-driven longitudinal analysis to study Amygdala and Thalamus atrophy patterns in Alzheimer's disease. The study uses advanced deep learning models to track structural changes over time and identify disease progression biomarkers.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 18
    },
    {
      id: 6,
      title: "Diagnostic utility of optic nerve sheath and ventricular indices in MRI-based detection of papilledema",
      description: "MRI-based diagnostic utility of optic nerve sheath and ventricular indices for papilledema detection",
      details: "This study investigates the diagnostic utility of optic nerve sheath diameter and ventricular indices in MRI-based detection of papilledema. The research employs quantitative image analysis techniques to develop reliable diagnostic markers for this neurological condition.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 15
    },
    {
      id: 7,
      title: "Automated longitudinal hippocampal subfield volumetry in Alzheimer's disease and mild cognitive impairment",
      description: "Automated longitudinal hippocampal subfield volumetry analysis in Alzheimer's disease and MCI",
      details: "This research presents an automated framework for longitudinal hippocampal subfield volumetry in Alzheimer's disease and mild cognitive impairment. The study utilizes advanced segmentation algorithms and longitudinal analysis to track subfield-specific changes over time, enabling early detection and progression monitoring.",
      status: "Accepted",
      venue: "European Congress of Radiology",
      coins: 20
    }
  ];

  const handleCoinCollect = () => {
    setCoins((prev) => prev + 1);
    toast.success("+1 Coin Collected!", {
      duration: 2000,
    });
  };

  const logIpEvent = async (event) => {
    try {
      // Fetch public IP and location from geolocation API
      let ip = 'unknown';
      let location = {
        city: 'unknown',
        country: 'unknown',
        latitude: null,
        longitude: null,
        timezone: 'unknown',
        isp: 'unknown',
      };

      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          ip = data.ip;
          location = {
            city: data.city || 'unknown',
            country: data.country_name || 'unknown',
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            timezone: data.timezone || 'unknown',
            isp: data.org || 'unknown',
          };
        }
      } catch (err) {
        console.debug('Failed to fetch IP and location:', err);
      }

      // Create log entry
      const logEntry = {
        event,
        path: window.location.pathname,
        ip,
        location,
        timestamp: new Date().toISOString(),
      };

      // Store in localStorage
      const existingLogs = JSON.parse(localStorage.getItem('ipLogs') || '[]');
      existingLogs.push(logEntry);
      localStorage.setItem('ipLogs', JSON.stringify(existingLogs));
    } catch (error) {
      console.debug('IP log failed', error?.message || error);
    }
  };

  const openSecretHeart = async () => {
    setSecretError('');
    setSecretPassword('');
    setSecretAccessGranted(false);
    setIpLogs([]);
    setSecretDialogOpen(true);
  };

  const handleSecretSubmit = async (e) => {
    e.preventDefault();
    setIsSecretSubmitting(true);
    setSecretError('');

    // Check password locally
    if (secretPassword === 'AcusonX300@') {
      setSecretAccessGranted(true);
      
      // Load IP logs from localStorage
      const logs = JSON.parse(localStorage.getItem('ipLogs') || '[]');
      setIpLogs(logs);
      
      toast.success("Secret access granted.", { duration: 3000 });
    } else {
      setSecretError("Incorrect password. Try again.");
    }
    
    setIsSecretSubmitting(false);
    setSecretPassword('');
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    if (!viewedProjectIds.includes(project.id)) {
      setViewedProjectIds((prev) => [...prev, project.id]);
      setCoins((prev) => prev + project.coins);
      toast.success(`+${project.coins} coins unlocked!`, { duration: 3000 });
    }
  };

  const handlePublicationSelect = (publication) => {
    setSelectedPublication(publication);
    if (!viewedPublicationIds.includes(publication.id)) {
      setViewedPublicationIds((prev) => [...prev, publication.id]);
      setCoins((prev) => prev + publication.coins);
      toast.success(`+${publication.coins} research coins!`, { duration: 3000 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || 'mzzzyjjj';

      await axios.post(`https://formspree.io/f/${FORMSPREE_ENDPOINT}`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        _subject: 'Portfolio contact request',
        _replyto: formData.email,
      });
      
      // Log form submission
      await logIpEvent('form-submitted');

      toast.success("Message sent! Thanks for reaching out! 🎮", {
        duration: 3000,
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error("Failed to send message. Please try again.", {
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] font-body">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="sticky top-0 z-50 bg-[hsl(var(--card))] border-b-[3px] border-[color:hsl(var(--pixel-outline))] shadow-md"
        data-testid="header"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 bg-[hsl(var(--primary))] border-[3px] border-[color:hsl(var(--pixel-outline))] flex items-center justify-center"
                data-testid="logo"
              >
                <Code className="w-6 h-6 text-white" />
              </motion.div>
              <span className="font-pixel text-lg font-bold" data-testid="logo-text">VISHESH</span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
              <button
                onClick={() => scrollToSection('about')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-about"
              >
                ABOUT
              </button>
              <button
                onClick={() => scrollToSection('experience')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-experience"
              >
                EXPERIENCE
              </button>
              <button
                onClick={() => scrollToSection('publications')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-publications"
              >
                RESEARCH
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-projects"
              >
                PROJECTS
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="font-press-start text-xs hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="nav-contact"
              >
                CONTACT
              </button>
            </nav>

            {/* Coin Counter */}
            <div className="flex items-center gap-3">
              {/* Dark Mode Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleDarkMode}
                className="w-10 h-10 bg-[hsl(var(--card))] dark:bg-[hsl(var(--card))] border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] flex items-center justify-center hover:bg-[hsl(var(--muted))] dark:hover:bg-[hsl(var(--muted))] transition-colors"
                data-testid="dark-mode-toggle"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-[hsl(var(--coin-gold))]" />
                ) : (
                  <Moon className="w-5 h-5 text-[hsl(var(--primary))]" />
                )}
              </motion.button>

              <motion.div
                whileHover={{ scale: 1.05 }}
                onClick={handleCoinCollect}
                className="flex items-center gap-2 px-3 py-2 bg-[hsl(var(--card))] dark:bg-[hsl(var(--card))] border-[3px] rounded-none border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] cursor-pointer hover-shake"
                data-testid="coin-counter"
              >
                <div className="w-5 h-5 bg-[hsl(var(--coin-gold))] rounded-full border-2 border-[color:hsl(var(--pixel-outline))]" />
                <span className="font-press-start text-xs">{coins}</span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section
        id="about"
        className="relative py-12 sm:py-16 lg:py-24 overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 pixel-grid opacity-50" />
        <div
          className="absolute inset-0"
          style={{
            background: isDarkMode 
              ? 'linear-gradient(135deg, hsl(222 47% 15%) 0%, hsl(222 47% 18%) 55%, hsl(222 47% 20%) 100%)'
              : 'linear-gradient(135deg, hsl(197 92% 96%) 0%, hsl(160 64% 96%) 55%, hsl(48 100% 96%) 100%)'
          }}
        />
        
        {/* Pixel Trail Effect */}
        <div className="absolute inset-0 z-0">
          <PixelTrail
            pixelSize={screenSize.lessThan("md") ? 40 : 60}
            fadeDuration={800}
            delay={0}
            pixelClassName="rounded-sm bg-[hsl(var(--coin-gold))] opacity-60"
          />
        </div>

        <div className="relative mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Avatar */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="flex justify-center"
              data-testid="hero-avatar"
            >
              <div className="relative">
                <div className="w-64 h-64 border-[4px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] flex items-center justify-center animate-float overflow-hidden bg-[hsl(var(--card))]">
                  <img 
                    src="https://res.cloudinary.com/dcvmvxbyf/image/upload/v1773252906/7DfhBbyJ_jdex6z.webp" 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-4 -right-4"
                >
                  <Star className="w-12 h-12 text-[hsl(var(--coin-gold))] fill-[hsl(var(--coin-gold))]" />
                </motion.div>
              </div>
            </motion.div>

            {/* Hero Content */}
            <div className="space-y-6" data-testid="hero-content">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className="font-pixel text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="hero-title">
                  VISHESH VARSHNEY
                </h1>
              </motion.div>
              
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="h-8"
              >
                <AnimatePresence mode="wait">
                  <motion.h2
                    key={currentTaglineIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="font-press-start text-base sm:text-lg text-[hsl(var(--primary))] tracking-wide"
                    data-testid="hero-subtitle"
                  >
                    {taglines[currentTaglineIndex]}
                  </motion.h2>
                </AnimatePresence>
              </motion.div>

              <motion.p
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-base sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))]"
                data-testid="hero-description"
              >
                Research Intern at United Imaging Healthcare, specializing in medical imaging AI and deep learning. 
                Published 7 research abstracts at the European Congress of Radiology. Building intelligent systems 
                that bridge technology and healthcare.
              </motion.p>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
                data-testid="hero-cta-container"
              >
                <Button
                  onClick={() => scrollToSection('projects')}
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs px-6 py-6"
                  data-testid="hero-primary-cta"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  VIEW PROJECTS
                </Button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  variant="outline"
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] font-press-start text-xs px-6 py-6"
                  data-testid="hero-secondary-cta"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  HIRE ME
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section
        id="experience"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--card))]"
        data-testid="experience-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="experience-title">
              WORK EXPERIENCE
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="experience-subtitle">
              Current quest in the professional realm
            </p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            data-testid="experience-card"
          >
            <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow p-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-[hsl(var(--primary))] border-[3px] border-[color:hsl(var(--pixel-outline))] flex items-center justify-center">
                    <Briefcase className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="font-pixel text-2xl mb-2" data-testid="experience-position">Research Intern</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white font-press-start text-xs px-3 py-1" data-testid="experience-company">
                        United Imaging Healthcare
                      </Badge>
                      <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--coin-gold))] text-[hsl(var(--foreground))] font-press-start text-xs px-3 py-1" data-testid="experience-duration">
                        June 2025 - Present
                      </Badge>
                    </div>
                  </div>
                  <p className="text-base text-[hsl(var(--muted-foreground))] leading-relaxed" data-testid="experience-description">
                    Conducting cutting-edge research in medical imaging, developing deep learning models for diagnostic 
                    applications, and automating analysis workflows. Successfully published 7 research abstracts at the 
                    European Congress of Radiology, contributing to advancements in neuroimaging and medical AI.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 px-3 py-2 bg-[hsl(var(--coin-gold))] border-2 border-[color:hsl(var(--pixel-outline))]">
                      <div className="w-4 h-4 bg-[hsl(var(--coin-gold))] rounded-full border-2 border-[color:hsl(var(--pixel-outline))]" />
                      <span className="font-press-start text-xs">50 XP EARNED</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Research Publications Section */}
      <section
        id="publications"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--muted))/30]"
        data-testid="publications-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="publications-title">
              RESEARCH ACHIEVEMENTS
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="publications-subtitle">
              7 abstracts published at European Congress of Radiology
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="publications-grid">
            {publications.map((publication, index) => (
              <motion.div
                key={publication.id}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                data-testid={`publication-card-${index}`}
              >
                <Card
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow cursor-pointer h-full"
                  onClick={() => handlePublicationSelect(publication)}
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <BookOpen className="w-6 h-6 text-[hsl(var(--primary))] flex-shrink-0 mt-1" />
                      <div className="flex items-center gap-1 px-2 py-1 bg-[hsl(var(--mint-green))] border-2 border-[color:hsl(var(--pixel-outline))]">
                        <span className="font-press-start text-xs text-white" data-testid={`publication-status-${index}`}>{publication.status}</span>
                      </div>
                    </div>
                    <h3 className="font-pixel text-sm leading-tight" data-testid={`publication-title-${index}`}>
                      {publication.title}
                    </h3>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-3" data-testid={`publication-description-${index}`}>
                      {publication.description}
                    </p>
                    <div className="flex items-center justify-between pt-2">
                      <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] font-press-start text-xs px-2 py-1">
                        ECR
                      </Badge>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-[hsl(var(--coin-gold))] rounded-full border border-[color:hsl(var(--pixel-outline))]" />
                        <span className="font-press-start text-xs" data-testid={`publication-coins-${index}`}>{publication.coins}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section
        id="skills"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--card))]"
        data-testid="skills-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="skills-title">
              PLAYER STATS
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="skills-subtitle">
              Skills leveled up through countless side quests
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6" data-testid="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ x: index % 2 === 0 ? -100 : 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`skill-item-${index}`}
              >
                <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-pixel text-lg" data-testid={`skill-name-${index}`}>{skill.name}</h3>
                    <span className="text-2xl">{skill.icon}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-press-start text-xs text-[hsl(var(--muted-foreground))]">LVL</span>
                      <span className="font-press-start text-xs" data-testid={`skill-level-${index}`}>{skill.level}</span>
                    </div>
                    <div
                      className="h-6 border-[3px] rounded-none border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] overflow-hidden"
                      data-testid={`skill-stat-bar-${index}`}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
                        className="h-full"
                        style={{ backgroundColor: `hsl(var(${skill.color}))` }}
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--muted))/30]"
        data-testid="projects-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="projects-title">
              QUEST LOG
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="projects-subtitle">
              Epic projects completed on the journey
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                data-testid={`project-card-${index}`}
              >
                <Card
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] hover:shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] transition-shadow cursor-pointer h-full"
                  onClick={() => handleProjectSelect(project)}
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <h3 className="font-pixel text-lg" data-testid={`project-title-${index}`}>{project.title}</h3>
                      <div className="flex items-center gap-1 px-2 py-1 bg-[hsl(var(--coin-gold))] border-2 border-[color:hsl(var(--pixel-outline))]">
                        <div className="w-3 h-3 bg-[hsl(var(--coin-gold))] rounded-full border border-[color:hsl(var(--pixel-outline))]" />
                        <span className="font-press-start text-xs" data-testid={`project-coins-${index}`}>{project.coins}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid={`project-description-${index}`}>
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, tagIndex) => (
                        <Badge
                          key={tag}
                          className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--primary))] hover:text-white font-press-start text-xs px-2 py-1"
                          data-testid={`project-tag-${index}-${tagIndex}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--card))]"
        data-testid="contact-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-3xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-pixel text-4xl sm:text-5xl font-bold mb-4" data-testid="contact-title">
              START A QUEST
            </h2>
            <p className="text-base text-[hsl(var(--muted-foreground))]" data-testid="contact-subtitle">
              Let's team up and build something legendary
            </p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] shadow-[6px_6px_0_0_hsl(var(--pixel-outline))] p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="name">
                    NAME
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] font-body"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="email">
                    EMAIL
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] font-body"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="font-press-start text-xs block mb-2" htmlFor="message">
                    MESSAGE
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell me about your project..."
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] min-h-[120px] font-body"
                    required
                    data-testid="contact-message-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] active:shadow-[0_0_0_0_hsl(var(--pixel-outline))] active:translate-x-[4px] active:translate-y-[4px] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="bg-[hsl(var(--card))] dark:bg-[hsl(var(--background))] border-t-[3px] border-[hsl(var(--pixel-outline))] text-[hsl(var(--foreground))] py-12"
        data-testid="footer"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div data-testid="footer-about">
              <h3 className="font-pixel text-xl mb-4">VISHESH VARSHNEY</h3>
            </div>

            {/* Quick Links */}
            <div data-testid="footer-links">
              <h3 className="font-pixel text-xl mb-4">QUICK LINKS</h3>
              <div className="space-y-2">
                <button
                  onClick={() => scrollToSection('about')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-about"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('experience')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-experience"
                >
                  Experience
                </button>
                <button
                  onClick={() => scrollToSection('publications')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-publications"
                >
                  Research
                </button>
                <button
                  onClick={() => scrollToSection('projects')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-projects"
                >
                  Projects
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block text-sm text-gray-300 hover:text-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-link-contact"
                >
                  Contact
                </button>
              </div>
            </div>

            {/* Social */}
            <div data-testid="footer-social">
              <h3 className="font-pixel text-xl mb-4">CONNECT</h3>
              <div className="flex gap-4">
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://github.com/visheshvarshney"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[hsl(var(--card))] border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-github"
                >
                  <Github className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4 }}
                  href="https://www.linkedin.com/company/united-imaging-healthcare"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[hsl(var(--card))] border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-linkedin"
                >
                  <Linkedin className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4 }}
                  href="mailto:varshneyvisheshin@gmail.com"
                  className="w-12 h-12 bg-[hsl(var(--card))] border-[3px] border-white flex items-center justify-center hover:bg-[hsl(var(--coin-gold))] transition-colors"
                  data-testid="footer-social-email"
                >
                  <Mail className="w-6 h-6 text-[hsl(var(--pixel-outline))]" />
                </motion.a>
              </div>
            </div>
          </div>

          <div className="border-t-[3px] border-white/20 pt-8 text-center">
            <p className="font-press-start text-xs text-gray-300" data-testid="footer-copyright">
              © 2025 VISHESH VARSHNEY. BUILT WITH <button
                type="button"
                onClick={openSecretHeart}
                className="inline-flex p-0 m-0 align-middle text-[hsl(var(--heart-red))] hover:opacity-80"
                aria-label="Open secret access"
                data-testid="secret-heart-button"
              >
                <Heart className="inline w-3 h-3 fill-[hsl(var(--heart-red))]" />
              </button> & REACT
            </p>
          </div>
        </div>
      </footer>

      {/* Secret Heart Dialog */}
      <Dialog open={secretDialogOpen} onOpenChange={setSecretDialogOpen}>
        <DialogContent className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] max-w-xl" data-testid="secret-heart-dialog">
          <DialogHeader>
            <DialogTitle className="font-pixel text-2xl">Access the hidden terminal</DialogTitle>
            <DialogDescription className="text-sm font-body leading-relaxed">
              Enter the secret password to unlock the hidden screen. This action is logged for security.
            </DialogDescription>
          </DialogHeader>

          {secretAccessGranted ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Welcome, agent. All IP access logs:</p>
              {ipLogs.length > 0 ? (
                <div className="space-y-2">
                  {ipLogs.map((log, idx) => (
                    <div key={idx} className="rounded-none border-[2px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--muted))] p-3">
                      <div className="font-press-start text-xs text-[hsl(var(--primary))] mb-1">{log.ip}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                        <div><span className="text-[hsl(var(--coin-gold))]">EVENT:</span> {log.event}</div>
                        <div><span className="text-[hsl(var(--coin-gold))]">PATH:</span> {log.path}</div>
                        <div><span className="text-[hsl(var(--coin-gold))]">TIME:</span> {new Date(log.timestamp).toLocaleString()}</div>
                        {log.location && (
                          <>
                            <div><span className="text-[hsl(var(--coin-gold))]">LOCATION:</span> {log.location.city}, {log.location.country}</div>
                            {log.location.latitude && log.location.longitude && (
                              <div><span className="text-[hsl(var(--coin-gold))]">COORDS:</span> {log.location.latitude.toFixed(4)}, {log.location.longitude.toFixed(4)}</div>
                            )}
                            <div><span className="text-[hsl(var(--coin-gold))]">TZ:</span> {log.location.timezone}</div>
                            <div><span className="text-[hsl(var(--coin-gold))]">ISP:</span> {log.location.isp}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--card))] p-4">
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">No logs recorded yet.</p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSecretSubmit} className="space-y-4">
              <div>
                <label className="font-press-start text-xs block mb-2" htmlFor="secret-password">PASSWORD</label>
                <Input
                  id="secret-password"
                  type="password"
                  value={secretPassword}
                  onChange={(e) => setSecretPassword(e.target.value)}
                  placeholder="Enter password"
                  className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] focus:ring-2 focus:ring-[hsl(var(--primary))] font-body"
                  required
                  data-testid="secret-password-input"
                />
                {secretError && <p className="mt-2 text-xs text-[hsl(var(--heart-red))]">{secretError}</p>}
              </div>
              <Button
                type="submit"
                disabled={isSecretSubmitting}
                className="w-full rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white font-press-start text-xs py-4 disabled:opacity-50"
                data-testid="secret-submit-button"
              >
                {isSecretSubmitting ? 'VERIFYING...' : 'UNLOCK'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] max-w-2xl" data-testid="project-dialog">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="font-pixel text-2xl" data-testid="project-dialog-title">
                  {selectedProject.title}
                </DialogTitle>
                <DialogDescription className="text-sm font-body leading-relaxed" data-testid="project-dialog-description">
                  {selectedProject.details}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag) => (
                    <Badge
                      key={tag}
                      className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white font-press-start text-xs px-3 py-1"
                      data-testid="project-dialog-tag"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-3 py-2 bg-[hsl(var(--coin-gold))] border-2 border-[color:hsl(var(--pixel-outline))]">
                    <div className="w-4 h-4 bg-[hsl(var(--coin-gold))] rounded-full border-2 border-[color:hsl(var(--pixel-outline))]" />
                    <span className="font-press-start text-xs">{selectedProject.coins} COINS EARNED</span>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Button
                    asChild
                    className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--sky-blue))] font-press-start text-xs"
                    data-testid="project-dialog-github-btn"
                  >
                    <a href={selectedProject.link} target="_blank" rel="noopener noreferrer">
                      <Github className="w-4 h-4 mr-2" />
                      VIEW CODE
                    </a>
                  </Button>
                  {selectedProject.demoLink && (
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[4px_4px_0_0_hsl(var(--pixel-outline))] font-press-start text-xs"
                      data-testid="project-dialog-demo-btn"
                    >
                      <a href={selectedProject.demoLink} target="_blank" rel="noopener noreferrer">
                        <Sparkles className="w-4 h-4 mr-2" />
                        LIVE DEMO
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Publication Dialog */}
      <Dialog open={!!selectedPublication} onOpenChange={() => setSelectedPublication(null)}>
        <DialogContent className="rounded-none border-[3px] border-[color:hsl(var(--pixel-outline))] shadow-[8px_8px_0_0_hsl(var(--pixel-outline))] max-w-2xl" data-testid="publication-dialog">
          {selectedPublication && (
            <>
              <DialogHeader>
                <DialogTitle className="font-pixel text-xl leading-tight mb-4" data-testid="publication-dialog-title">
                  {selectedPublication.title}
                </DialogTitle>
                <DialogDescription className="text-sm font-body leading-relaxed" data-testid="publication-dialog-description">
                  {selectedPublication.details}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--mint-green))] text-white font-press-start text-xs px-3 py-1">
                    {selectedPublication.status}
                  </Badge>
                  <Badge className="rounded-none border-2 border-[color:hsl(var(--pixel-outline))] bg-[hsl(var(--primary))] text-white font-press-start text-xs px-3 py-1">
                    {selectedPublication.venue}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-3 py-2 bg-[hsl(var(--coin-gold))] border-2 border-[color:hsl(var(--pixel-outline))]">
                    <div className="w-4 h-4 bg-[hsl(var(--coin-gold))] rounded-full border-2 border-[color:hsl(var(--pixel-outline))]" />
                    <span className="font-press-start text-xs">{selectedPublication.coins} RESEARCH POINTS</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
